import { notify } from "../../util/notify";
import { focusFirstError, readApiErrors } from "../../util/formErrors";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useHistory } from "react-router-dom";
import axios from "axios";
import useCheckoutQuote, { fetchCheckoutQuote } from "../../hooks/useCheckoutQuote";
import { imageurl } from "../../App";
import {
  getGuestCart,
  getGuestCartTotals,
  clearGuestCart,
  CART_UPDATED_EVENT,
  setStoreFreeShippingThreshold
} from "../../util/guestCartService";
import { getProductImageUrl, handleProductImageError, applyStoreTitle } from "../../util/storeLogoHelper";
import { customerName, isCustomerLoggedIn } from "../../util/customerAuth";
import { loadStoreSettings, peekStoreSettings } from "../../util/storeSettings";
import { configureMetaPixel, trackMetaPixel } from "../../util/metaPixel";
import {
  FaTag,
  FaCheckCircle,
  FaShieldAlt,
  FaTruck,
  FaBolt,
  FaMapMarkerAlt,
  FaChevronDown,
  FaChevronUp,
  FaTimes,
  FaCheck,
  FaClock
} from "react-icons/fa";

const MAJOR_CITIES_LIST = [
  "Karachi", "Lahore", "Islamabad", "Rawalpindi", "Faisalabad", "Multan",
  "Peshawar", "Quetta", "Gujranwala", "Sialkot", "Hyderabad",
  "Abbottabad", "Bahawalpur", "Sukkur", "Sargodha", "Larkana",
  "Sheikhupura", "Jhang", "Rahim Yar Khan", "Gujrat", "Mardan",
  "Kasur", "Sahiwal", "Okara", "Wah Cantt", "Dera Ghazi Khan",
  "Mirpur Khas", "Nawabshah", "Chiniot", "Kamoke", "Hafizabad"
];

function isEmailAddress(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function mobileDigits(value) {
  let digits = String(value || "").replace(/\D/g, "");
  if (digits.indexOf("0092") === 0) {
    digits = digits.substring(4);
  } else if (digits.indexOf("92") === 0 && digits.length > 11) {
    digits = digits.substring(2);
  }
  if (digits.charAt(0) === "3") {
    digits = "0" + digits;
  }
  return /^03\d{9}$/.test(digits) ? digits : "";
}

function browserCookie(name) {
  if (typeof document === "undefined") return "";
  const prefix = `${name}=`;
  const row = document.cookie.split(";").map((part) => part.trim()).find((part) => part.indexOf(prefix) === 0);
  return row ? decodeURIComponent(row.slice(prefix.length)) : "";
}

function isMobileNumber(value) {
  return mobileDigits(value) !== "";
}

function FieldMessage({ message }) {
  if (!message) return null;
  return (
    <div style={{ marginTop: "6px", fontSize: "12.5px", lineHeight: "1.4", color: "#b91c1c" }}>
      {message}
    </div>
  );
}

export default function Checkout() {
  const history = useHistory();
  const [submitting, setSubmitting] = useState(false);
  const [cartData, setCartData] = useState(() => getGuestCartTotals());
  const [discountCode, setDiscountCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  // Shown in the page instead of popups.
  const [couponMessage, setCouponMessage] = useState("");
  const [orderError, setOrderError] = useState("");
  const [storeSettings, setStoreSettings] = useState(null);

  // Form State matching Jenpharm / Shopify checkout fields
  const [formData, setFormData] = useState({
    contact: "",
    emailNews: true,
    country: "Pakistan",
    firstname: "",
    lastname: "",
    address: "",
    city: "",
    postalCode: "",
    phone: "",
    saveInfo: true,
    textNews: false,
    shippingMethod: "standard",
    paymentMethod: "COD",
    billingSame: "same",
  });

  const [errors, setErrors] = useState({});
  const [paymentSlip, setPaymentSlip] = useState(null);
  const [paymentSlipPreview, setPaymentSlipPreview] = useState("");

  const realBankDetails = (value) => {
    const text = String(value || "").replace(/\r\n/g, "\n").trim();
    const sample = "Bank Name: Meezan Bank\nAccount Title: Store Name\nIBAN: PK00MEZN0000000000000000";
    return text && text !== sample ? text : "";
  };
  const settingOn = (value, fallback = true) => {
    if (value === undefined || value === null || value === "") return fallback;
    return value !== false && value !== "false" && value !== 0 && value !== "0";
  };
  const codEnabled = settingOn(storeSettings?.enableCOD, true);
  const bankEnabled = settingOn(storeSettings?.enableBankTransfer, true);
  // Online gateways appear only when the store owner switched them on with every key saved.
  const jazzcashEnabled = storeSettings?.enableJazzcash === true;
  const easypaisaEnabled = storeSettings?.enableEasypaisa === true;
  const paysOnline = formData.paymentMethod === "JAZZCASH" || formData.paymentMethod === "EASYPAISA";
  const onlineLabels = { JAZZCASH: "JazzCash", EASYPAISA: "Easypaisa" };

  useEffect(() => {
    const allowed = [
      codEnabled && "COD",
      bankEnabled && "BT",
      jazzcashEnabled && "JAZZCASH",
      easypaisaEnabled && "EASYPAISA",
    ].filter(Boolean);
    setFormData((prev) => (
      allowed.length && !allowed.includes(prev.paymentMethod) ? { ...prev, paymentMethod: allowed[0] } : prev
    ));
  }, [codEnabled, bankEnabled, jazzcashEnabled, easypaisaEnabled]);

  useEffect(() => {
    const email = (formData.contact || "").trim();
    if (email.indexOf("@") === -1 || !cartData.items || cartData.items.length === 0) {
      return undefined;
    }
    const timer = setTimeout(() => {
      axios.post("/api/checkout-reminder", {
        email,
        name: `${formData.firstname || ""} ${formData.lastname || ""}`.trim(),
        items: cartData.items.map((item) => ({
          name: item.name,
          qty: item.qty,
          price: item.price,
          image: typeof item.image === "string" ? item.image : "",
        })),
      }).catch(() => {});
    }, 4000);
    return () => clearTimeout(timer);
  }, [formData.contact, formData.firstname, formData.lastname, cartData.items]);

  useEffect(() => {
    if (!isCustomerLoggedIn()) return undefined;
    axios.get("/api/user-profile").then((res) => {
      const user = res.data?.user;
      if (!user) return;
      const parts = String(user.name || "").trim().split(" ");
      const first = parts.shift() || "";
      const last = parts.join(" ");
      setFormData((prev) => ({
        ...prev,
        contact: prev.contact || user.email || "",
        firstname: prev.firstname || first,
        lastname: prev.lastname || last,
      }));
    }).catch(() => {});
    axios.get("/api/user-addresses").then((res) => {
      const list = res.data?.addresses || [];
      const saved = list.find((row) => row.is_default) || list[0];
      if (!saved) return;
      const parts = String(saved.full_name || "").trim().split(" ");
      const first = parts.shift() || "";
      const last = parts.join(" ");
      setFormData((prev) => ({
        ...prev,
        firstname: prev.firstname || first,
        lastname: prev.lastname || last,
        phone: prev.phone || saved.phone || "",
        address: prev.address || saved.address || "",
        city: prev.city || saved.city || "",
        postalCode: prev.postalCode || saved.zipcode || "",
      }));
    }).catch(() => {});
    return undefined;
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    applyStoreTitle(null, "Checkout");

    loadStoreSettings().then((settings) => {
      if (settings && (settings.storeName || settings.logoUrl || settings.supportEmail)) {
        setStoreSettings(settings);
        if (settings.freeShippingThreshold !== undefined) {
          setStoreFreeShippingThreshold(settings.freeShippingThreshold);
        }
      }
    }).catch(() => {});

    const currentItems = getGuestCart();
    if (currentItems.length === 0) {
      history.replace("/collection");
    } else {
      const totals = getGuestCartTotals();
      configureMetaPixel().then((configured) => {
        if (!configured) return;
        trackMetaPixel("InitiateCheckout", {
          content_ids: currentItems.map((item) => String(item.product_id || item.id).split(":")[0]),
          content_type: "product",
          num_items: currentItems.reduce((sum, item) => sum + Number(item.qty || 0), 0),
          value: Number(totals.subtotal || 0),
          currency: peekStoreSettings()?.currency || "PKR",
          contents: currentItems.map((item) => ({
            id: String(item.product_id || item.id).split(":")[0],
            quantity: Number(item.qty || 1),
          })),
        });
      }).catch(() => {});
    }

    const handleCartUpdate = () => {
      setCartData(getGuestCartTotals());
    };

    window.addEventListener(CART_UPDATED_EVENT, handleCartUpdate);
    return () => {
      window.removeEventListener(CART_UPDATED_EVENT, handleCartUpdate);
    };
  }, [history]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Coupon check: the server prices the cart with the code and says whether it applies.
  const handleApplyCoupon = (e) => {
    e.preventDefault();
    const code = discountCode.trim();
    if (!code) {
      setCouponMessage("Enter a discount code.");
      return;
    }

    setCouponMessage("");
    setValidatingCoupon(true);
    fetchCheckoutQuote({ items: cartData.items, city: formData.city, shippingMethod: formData.shippingMethod, couponCode: code })
      .then((data) => {
        setValidatingCoupon(false);
        if (data.status === 200 && data.coupon?.code) {
          setAppliedCoupon({ code: data.coupon.code, discount_amount: data.coupon.discount });
          notify("Discount code applied");
        } else {
          setCouponMessage(data.coupon?.error || data.message || "This discount code is not valid.");
        }
      })
      .catch(() => {
        setValidatingCoupon(false);
        setCouponMessage("Could not check the discount code. Please try again.");
      });
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setDiscountCode("");
  };

  // Totals come from the server, priced exactly as the order will be.
  const { quote, loading: quoteLoading, error: quoteError } = useCheckoutQuote({
    items: cartData.items,
    city: formData.city,
    shippingMethod: formData.shippingMethod,
    couponCode: appliedCoupon ? appliedCoupon.code : null,
  });

  // If the cart changes so the coupon no longer applies, drop it and say why.
  useEffect(() => {
    if (appliedCoupon && quote?.coupon?.error) {
      setAppliedCoupon(null);
      setCouponMessage(`Discount code removed: ${quote.coupon.error}`);
    }
  }, [quote, appliedCoupon]);

  // Delivery cities as set in Store settings.
  const configuredCities = useMemo(() => quote?.cities || [], [quote]);

  // Professional City Combobox Dropdown State & Refs
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const [cityHighlightedIndex, setCityHighlightedIndex] = useState(-1);
  const cityContainerRef = useRef(null);
  const cityInputRef = useRef(null);

  // Close city dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (cityContainerRef.current && !cityContainerRef.current.contains(e.target)) {
        setCityDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  // Filtered Cities for Dropdown with exact-match awareness
  const filteredCities = useMemo(() => {
    const query = (formData.city || "").trim().toLowerCase();
    if (!query) return configuredCities;

    const exactMatch = configuredCities.some(
      (c) => (c.cityName || "").trim().toLowerCase() === query
    );
    if (exactMatch) {
      return configuredCities;
    }

    return configuredCities.filter((c) =>
      (c.cityName || "").toLowerCase().includes(query)
    );
  }, [configuredCities, formData.city]);

  const hasExactCityMatch = useMemo(() => {
    const query = (formData.city || "").trim().toLowerCase();
    if (!query) return false;
    return configuredCities.some(
      (c) => (c.cityName || "").trim().toLowerCase() === query
    );
  }, [configuredCities, formData.city]);

  const handleSelectCity = (cityName) => {
    setFormData((prev) => ({ ...prev, city: cityName }));
    if (errors.city) {
      setErrors((prev) => ({ ...prev, city: null }));
    }
    setCityDropdownOpen(false);
  };

  const handleClearCity = (e) => {
    e.stopPropagation();
    setFormData((prev) => ({ ...prev, city: "" }));
    setCityDropdownOpen(true);
    if (cityInputRef.current) {
      cityInputRef.current.focus();
    }
  };

  const handleCityKeyDown = (e) => {
    if (!cityDropdownOpen) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setCityDropdownOpen(true);
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCityHighlightedIndex((prev) =>
        prev < filteredCities.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCityHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredCities.length - 1
      );
    } else if (e.key === "Enter") {
      if (cityHighlightedIndex >= 0 && cityHighlightedIndex < filteredCities.length) {
        e.preventDefault();
        handleSelectCity(filteredCities[cityHighlightedIndex].cityName);
      } else if (formData.city.trim()) {
        e.preventDefault();
        setCityDropdownOpen(false);
      }
    } else if (e.key === "Escape") {
      setCityDropdownOpen(false);
    }
  };

  // Shipping as worked out by the server for this city and method.
  const dynamicShipping = useMemo(() => {
    const ship = quote?.shipping;
    if (!ship) {
      return {
        hasAddress: Boolean((formData.city || "").trim()),
        zone: "uncalculated",
        zoneLabel: "Calculated from city",
        standardRate: 0,
        expressRate: 0,
        finalFee: 0,
        isFreeOrder: false,
        freeThreshold: 0,
        remainingForFree: 0,
        matchedCityObj: null,
      };
    }
    return {
      hasAddress: ship.has_city,
      zone: ship.city_matched ? "city" : (ship.has_city ? "default" : "uncalculated"),
      zoneLabel: ship.label,
      standardRate: ship.standard_fee,
      expressRate: ship.express_fee,
      finalFee: ship.fee,
      isFreeOrder: ship.is_free,
      freeThreshold: ship.free_threshold,
      remainingForFree: ship.remaining_for_free,
      matchedCityObj: ship.city_matched ? { estimatedDays: ship.estimated_days } : null,
    };
  }, [quote, formData.city]);

  const dynamicShippingFee = dynamicShipping.finalFee;
  const subtotal = quote ? quote.subtotal : cartData.subtotal;
  const couponDiscount = appliedCoupon && quote ? Number(quote.coupon?.discount || 0) : 0;
  const grandTotal = quote ? quote.total : subtotal;
  const totalCombinedSavings = cartData.totalSavings + couponDiscount + Number(quote?.bundles?.discount || 0);

  // Complete Order
  const handleCompleteOrder = (e) => {
    e.preventDefault();

    const newErrors = {};
    const firstName = formData.firstname.trim();
    const lastName = formData.lastname.trim();
    const address = formData.address.trim();
    const city = formData.city.trim();
    const phone = formData.phone.trim();
    const email = formData.contact.trim();

    if (!firstName) newErrors.firstname = "Enter your first name";
    if (!lastName) newErrors.lastname = "Enter your last name";
    if (!address) newErrors.address = "Enter the house number, street, and area";
    if (!city) newErrors.city = "Choose your city";
    if (!phone) newErrors.phone = "Enter your phone number. This is how we reach you about the order.";
    else if (!isMobileNumber(phone)) {
      newErrors.phone = "Enter a mobile number as 0300 1234567, +92 300 1234567, or 0092 300 1234567.";
    }
    if (email && !isEmailAddress(email)) newErrors.contact = "Enter a valid email, or leave this blank";
    if (formData.paymentMethod === "BT" && !paymentSlip) {
      newErrors.payment_slip = "Upload a photo of the transfer.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      window.setTimeout(() => {
        const field = document.querySelector(".checkout-field.is-invalid, .is-invalid");
        if (field && field.scrollIntoView) {
          field.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 0);
      setOrderError(newErrors.payment_slip && Object.keys(newErrors).length === 1
        ? "Upload a photo of the bank transfer before placing the order."
        : "Please check the highlighted fields.");
      return;
    }

    setSubmitting(true);

    const guestCartItems = getGuestCart();
    const emailToUse = email && isEmailAddress(email)
      ? email
      : `${mobileDigits(phone)}@guest.getgrowcart.com`;

    const payload = {
      firstname: formData.firstname.trim(),
      lastname: formData.lastname.trim(),
      phone: phone,
      email: emailToUse,
      address: formData.address.trim(),
      city: formData.city.trim(),
      state: formData.country,
      zipcode: formData.postalCode.trim(),
      paymentType: formData.paymentMethod,
      shipping_method: formData.shippingMethod,
      cart_items: guestCartItems,
      coupon_code: appliedCoupon ? appliedCoupon.code : null,
      coupon_discount: couponDiscount,
      shipping_fee: dynamicShippingFee,
      total_amount: grandTotal,
      meta_fbp: browserCookie("_fbp"),
      meta_fbc: browserCookie("_fbc"),
      event_source_url: window.location.href,
    };

    let requestBody = payload;
    let requestConfig;
    if (formData.paymentMethod === "BT") {
      requestBody = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        if (key === "cart_items") {
          requestBody.append(key, JSON.stringify(value));
          return;
        }
        requestBody.append(key, value);
      });
      requestBody.append("payment_slip", paymentSlip);
      requestConfig = { headers: { "Content-Type": "multipart/form-data" } };
    }

    // Server problems: under the fields they belong to, plus one line by the button.
    const showOrderErrors = (source) => {
      const { message, fields } = readApiErrors(source);
      setErrors(fields);
      setOrderError(message || "Could not place the order. Please try again.");
      focusFirstError();
    };
    setOrderError("");
    axios
      .post(`/api/place-order`, requestBody, requestConfig)
      .then((res) => {
        setSubmitting(false);
        if (res.data.status === 200) {
          const orderId = res.data.order_id || res.data.order?.id || "1001";
          const trackingNo = res.data.tracking_no || "";

          // Clear local cart
          clearGuestCart();

          // Save last order info for success page
          sessionStorage.setItem(
            "last_order_details",
            JSON.stringify({
              orderId,
              trackingNo,
              customerName: `${formData.firstname} ${formData.lastname}`,
              email: emailToUse,
              phone: formData.phone,
              address: `${formData.address}, ${formData.city}`,
              paymentMethod: formData.paymentMethod === "BT"
                ? "Bank transfer"
                : paysOnline ? `${onlineLabels[formData.paymentMethod]} (paid online)` : "Cash on Delivery (COD)",
              paymentSlipPending: formData.paymentMethod === "BT",
              shippingFee: dynamicShippingFee,
              totalAmount: grandTotal,
              items: guestCartItems,
              savings: totalCombinedSavings,
            })
          );

          // JazzCash / Easypaisa: the order is saved; now pay on the gateway's page.
          if (res.data.payment_url) {
            window.location.assign(res.data.payment_url);
            return;
          }
          history.push(`/order-success/${orderId}`);
        } else {
          showOrderErrors(res);
        }
      })
      .catch((err) => {
        setSubmitting(false);
        showOrderErrors(err);
      });
  };

  const { items } = cartData;

  return (
    <div className="checkout-page" style={{ minHeight: "100vh", fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <style>{`
        .checkout-page {
          background: #ffffff;
          color: #1c1917;
        }
        .checkout-page .checkout-heading {
          margin: 0 0 22px;
          font-size: 28px;
          letter-spacing: -0.04em;
          font-weight: 700;
        }
        .checkout-page .bg-white.border {
          border: 1px solid #e7e5e4 !important;
          border-radius: 20px !important;
          box-shadow: none !important;
        }
        .checkout-page h5 {
          letter-spacing: -0.03em;
          color: #1c1917 !important;
        }
        .checkout-page .form-control,
        .checkout-page .form-select {
          min-height: 48px;
          border-radius: 12px !important;
          border-color: #e7e5e4 !important;
          background: #ffffff !important;
          box-shadow: none !important;
          color: #1c1917;
        }
        .checkout-page .form-control:focus,
        .checkout-page .form-select:focus {
          background: #ffffff !important;
          border-color: #1c1917 !important;
          box-shadow: 0 0 0 4px rgba(28, 25, 23, 0.08) !important;
        }
        .checkout-page .form-control.is-invalid,
        .checkout-page .checkout-field.is-invalid {
          border-color: #b91c1c !important;
          background: #fff !important;
        }
        .checkout-page .checkout-option {
          border-radius: 14px !important;
          border: 1px solid #e7e5e4 !important;
          background: #fff !important;
          box-shadow: none !important;
        }
        .checkout-page .checkout-option.is-on {
          border-color: #1c1917 !important;
          background: #ffffff !important;
        }
        .checkout-pay-badge {
          flex-shrink: 0; padding: 4px 10px; border-radius: 999px;
          font-size: 11.5px; font-weight: 700; white-space: nowrap;
        }
        .checkout-pay-badge.is-jazzcash { background: #fee2e2; color: #b91c1c; }
        .checkout-pay-badge.is-easypaisa { background: #dcfce7; color: #15803d; }
        .checkout-page .btn-primary {
          border-radius: 14px !important;
          background: #1c1917 !important;
          border-color: #1c1917 !important;
        }
        .checkout-page .btn-primary:hover:not(:disabled) {
          background: #292524 !important;
        }
        .checkout-page .checkout-summary {
          top: 88px;
        }
        @media (max-width: 991px) {
          .checkout-page .checkout-summary { position: static !important; }
        }
      `}</style>
      <div className="container py-4 py-md-5">
        <h1 className="checkout-heading">Checkout</h1>
        <div className="row g-4 g-lg-5">
          {/* LEFT COLUMN: Customer, Shipping & Payment Form */}
          <div className="col-12 col-lg-7">
            <form noValidate onSubmit={handleCompleteOrder} className="d-flex flex-column gap-4">
              {/* 1. Contact Section */}
              <div className="bg-white p-4 rounded-3 border" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="fw-bold mb-0" style={{ fontSize: "17px", color: "#1e293b" }}>
                    Contact Information
                  </h5>
                  {isCustomerLoggedIn() ? (
                    <span className="text-muted" style={{ fontSize: "13px" }}>{customerName() || "Signed in"}</span>
                  ) : (
                    <Link to="/login?redirect=/checkout" className="fw-bold text-decoration-none" style={{ fontSize: "13.5px", color: "#111111" }}>
                      Sign in
                    </Link>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label mb-1" style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>
                    Email <span style={{ fontWeight: 500 }}>(optional)</span>
                  </label>
                  <input
                    type="email"
                    name="contact"
                    className={`form-control py-3 px-3 checkout-field ${errors.contact ? "is-invalid" : ""}`}
                    style={{ fontSize: "14px", borderRadius: "8px" }}
                    placeholder="name@email.com"
                    value={formData.contact}
                    onChange={handleInputChange}
                    autoComplete="email"
                  />
                  <FieldMessage message={errors.contact} />
                  {!errors.contact && (
                    <div style={{ marginTop: "6px", fontSize: "12px", color: "#64748b" }}>
                      Leave this blank if you only want updates by phone.
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label mb-1" style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>
                    Phone number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    className={`form-control py-3 px-3 checkout-field ${errors.phone ? "is-invalid" : ""}`}
                    style={{ fontSize: "14px", borderRadius: "8px" }}
                    placeholder="03210000000"
                    value={formData.phone}
                    onChange={handleInputChange}
                    autoComplete="tel"
                  />
                  <FieldMessage message={errors.phone} />
                  {!errors.phone && (
                    <div style={{ marginTop: "6px", fontSize: "12px", color: "#64748b" }}>
                      0300 1234567, +92 300 1234567, or 0092 300 1234567.
                    </div>
                  )}
                </div>

                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="emailNews"
                    name="emailNews"
                    checked={formData.emailNews}
                    onChange={handleInputChange}
                  />
                  <label className="form-check-label text-muted" htmlFor="emailNews" style={{ fontSize: "13px" }}>
                    Email me with news and offers
                  </label>
                </div>
              </div>

              {/* 2. Delivery Section */}
              <div className="bg-white p-4 rounded-3 border" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                <h5 className="fw-bold mb-3" style={{ fontSize: "17px", color: "#1e293b" }}>
                  Delivery
                </h5>

                <div className="row g-3">
                  {/* Country/Region */}
                  <div className="col-12">
                    <label className="form-label text-muted mb-1" style={{ fontSize: "12px", fontWeight: 600 }}>
                      Country/Region
                    </label>
                    <select
                      className="form-select py-2 px-3"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      style={{ fontSize: "14px", borderRadius: "8px", background: "#f8fafc" }}
                    >
                      <option value="Pakistan">Pakistan</option>
                      <option value="United Arab Emirates">United Arab Emirates</option>
                      <option value="United Kingdom">United Kingdom</option>
                      <option value="United States">United States</option>
                    </select>
                  </div>

                  {/* First Name & Last Name */}
                  <div className="col-md-6">
                    <label className="form-label mb-1" style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>
                      First name
                    </label>
                    <input
                      type="text"
                      name="firstname"
                      className={`form-control py-2 px-3 checkout-field ${errors.firstname ? "is-invalid" : ""}`}
                      style={{ fontSize: "14px", borderRadius: "8px" }}
                      placeholder="First name"
                      value={formData.firstname}
                      onChange={handleInputChange}
                      autoComplete="given-name"
                    />
                    <FieldMessage message={errors.firstname} />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label mb-1" style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>
                      Last name
                    </label>
                    <input
                      type="text"
                      name="lastname"
                      className={`form-control py-2 px-3 checkout-field ${errors.lastname ? "is-invalid" : ""}`}
                      style={{ fontSize: "14px", borderRadius: "8px" }}
                      placeholder="Last name"
                      value={formData.lastname}
                      onChange={handleInputChange}
                      autoComplete="family-name"
                    />
                    <FieldMessage message={errors.lastname} />
                  </div>

                  {/* Address */}
                  <div className="col-12">
                    <label className="form-label mb-1" style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>
                      Address
                    </label>
                    <input
                      type="text"
                      name="address"
                      className={`form-control py-2 px-3 checkout-field ${errors.address ? "is-invalid" : ""}`}
                      style={{ fontSize: "14px", borderRadius: "8px" }}
                      placeholder="House number, street, and area"
                      value={formData.address}
                      onChange={handleInputChange}
                      autoComplete="street-address"
                    />
                    <FieldMessage message={errors.address} />
                  </div>

                  {/* City & Postal Code */}
                  <div className="col-12 position-relative" ref={cityContainerRef} style={{ zIndex: cityDropdownOpen ? 30 : 1 }}>
                    <label className="form-label mb-1" style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>
                      City
                    </label>
                    <div
                      className="position-relative d-flex align-items-center"
                      style={{
                        borderRadius: "8px",
                        boxShadow: cityDropdownOpen
                          ? "0 0 0 4px rgba(28, 25, 23, 0.08)"
                          : "none",
                        transition: "box-shadow 0.18s ease-in-out",
                      }}
                    >
                      {/* Left Map Marker Icon */}
                      <div
                        className="position-absolute d-flex align-items-center justify-content-center"
                        style={{
                          left: "12px",
                          zIndex: 2,
                          pointerEvents: "none",
                          color: formData.city ? "#1c1917" : "#a8a29e",
                          transition: "color 0.2s ease",
                        }}
                      >
                        <FaMapMarkerAlt size={14} />
                      </div>

                      {/* Interactive Combobox Input */}
                      <input
                        ref={cityInputRef}
                        type="text"
                        name="city"
                        autoComplete="off"
                        className={`form-control checkout-field ${errors.city ? "is-invalid" : ""}`}
                        style={{
                          paddingLeft: "36px",
                          paddingRight: formData.city ? "62px" : "36px",
                          height: "48px",
                          fontSize: "14px",
                          fontWeight: formData.city ? "600" : "400",
                          borderRadius: "12px",
                          border: errors.city
                            ? "1px solid #b91c1c"
                            : cityDropdownOpen
                            ? "1px solid #1c1917"
                            : "1px solid #e7e5e4",
                          backgroundColor: "#ffffff",
                          color: "#1e293b",
                          transition: "border-color 0.18s ease",
                        }}
                        placeholder="Choose a city"
                        value={formData.city}
                        onFocus={() => setCityDropdownOpen(true)}
                        onClick={() => setCityDropdownOpen(true)}
                        onChange={(e) => {
                          handleInputChange(e);
                          setCityDropdownOpen(true);
                          setCityHighlightedIndex(-1);
                        }}
                        onKeyDown={handleCityKeyDown}
                      />

                      {/* Right-side Controls: Clear button + Dropdown Chevron */}
                      <div
                        className="position-absolute d-flex align-items-center"
                        style={{ right: "10px", zIndex: 3, gap: "4px" }}
                      >
                        {formData.city && (
                          <button
                            type="button"
                            onClick={handleClearCity}
                            title="Clear city selection"
                            className="btn btn-sm p-0 d-flex align-items-center justify-content-center"
                            style={{
                              width: "22px",
                              height: "22px",
                              borderRadius: "50%",
                              backgroundColor: "#f1f5f9",
                              color: "#64748b",
                              border: "none",
                              transition: "all 0.15s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "#e2e8f0";
                              e.currentTarget.style.color = "#0f172a";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = "#f1f5f9";
                              e.currentTarget.style.color = "#64748b";
                            }}
                          >
                            <FaTimes size={10} />
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={() => {
                            setCityDropdownOpen((prev) => !prev);
                            if (!cityDropdownOpen && cityInputRef.current) {
                              cityInputRef.current.focus();
                            }
                          }}
                          className="btn btn-sm p-0 d-flex align-items-center justify-content-center"
                          style={{
                            width: "24px",
                            height: "24px",
                            color: cityDropdownOpen ? "#2563eb" : "#94a3b8",
                            backgroundColor: "transparent",
                            border: "none",
                            transition: "color 0.15s ease",
                          }}
                        >
                          {cityDropdownOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                        </button>
                      </div>
                    </div>

                    <FieldMessage message={errors.city} />

                    {/* Custom Floating Dropdown Menu */}
                    {cityDropdownOpen && (
                      <div
                        className="position-absolute bg-white border"
                        style={{
                          top: "calc(100% + 4px)",
                          left: 0,
                          right: 0,
                          zIndex: 1060,
                          borderRadius: "10px",
                          borderColor: "#e2e8f0",
                          boxShadow: "0 18px 36px -6px rgba(15, 23, 42, 0.16), 0 8px 16px -4px rgba(15, 23, 42, 0.08)",
                          overflow: "hidden",
                        }}
                      >
                        {/* Dropdown Header */}
                        <div
                          className="d-flex align-items-center justify-content-between px-3 py-2"
                          style={{
                            backgroundColor: "#f8fafc",
                            borderBottom: "1px solid #f1f5f9",
                            fontSize: "11px",
                            fontWeight: "600",
                            color: "#64748b",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          <span className="d-flex align-items-center gap-1">
                            <FaMapMarkerAlt size={10} className="text-primary" /> Delivery Destinations
                          </span>
                          <span
                            className="badge"
                            style={{
                              backgroundColor: "#ffffff",
                              color: "#475569",
                              border: "1px solid #e2e8f0",
                              fontSize: "10px",
                              fontWeight: "600",
                              padding: "2px 6px",
                            }}
                          >
                            {filteredCities.length} Cities
                          </span>
                        </div>

                        {/* Scrollable Cities List */}
                        <div className="city-dropdown-scroll" style={{ maxHeight: "230px", overflowY: "auto" }}>
                          {filteredCities.map((c, index) => {
                            const isSelected =
                              (formData.city || "").toLowerCase().trim() ===
                              (c.cityName || "").toLowerCase().trim();
                            const isHighlighted = cityHighlightedIndex === index;

                            return (
                              <div
                                key={c.id || c.cityName}
                                onClick={() => handleSelectCity(c.cityName)}
                                onMouseEnter={() => setCityHighlightedIndex(index)}
                                className="city-item-hover d-flex align-items-center justify-content-between px-3 py-2"
                                style={{
                                  cursor: "pointer",
                                  backgroundColor: isSelected
                                    ? "#eff6ff"
                                    : isHighlighted
                                    ? "#f8fafc"
                                    : "transparent",
                                  borderLeft: isSelected
                                    ? "3.5px solid #2563eb"
                                    : "3.5px solid transparent",
                                  transition: "all 0.12s ease",
                                }}
                              >
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    style={{
                                      color: isSelected ? "#2563eb" : "#94a3b8",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    {isSelected ? <FaCheck size={11} className="text-primary" /> : <FaMapMarkerAlt size={11} />}
                                  </div>
                                  <span
                                    style={{
                                      fontSize: "14px",
                                      fontWeight: isSelected ? "700" : "500",
                                      color: isSelected ? "#1d4ed8" : "#1e293b",
                                    }}
                                  >
                                    {c.cityName}
                                  </span>
                                </div>
                              </div>
                            );
                          })}

                          {/* Custom Unlisted City Tile */}
                          {formData.city.trim() && !hasExactCityMatch && (
                            <div
                              onClick={() => handleSelectCity(formData.city.trim())}
                              className="px-3 py-2 d-flex align-items-center justify-content-between"
                              style={{
                                backgroundColor: "#fefce8",
                                borderTop: "1px dashed #fde047",
                                cursor: "pointer",
                                transition: "background 0.15s ease",
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#fef9c3")}
                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fefce8")}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <FaTruck size={12} className="text-warning" />
                                <div>
                                  <div style={{ fontSize: "12px", fontWeight: "700", color: "#854d0e" }}>
                                    Deliver to "{formData.city.trim()}"
                                  </div>
                                  <div style={{ fontSize: "10.5px", color: "#a16207" }}>
                                    Standard Nationwide Courier • 3–5 Business Days
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {filteredCities.length === 0 && !formData.city.trim() && (
                            <div className="p-3 text-center text-muted" style={{ fontSize: "12px" }}>
                              No direct cities found. Type your city name to proceed.
                            </div>
                          )}
                        </div>

                        {/* Dropdown Footer Hint */}
                        <div
                          className="px-3 py-1 d-flex align-items-center justify-content-between"
                          style={{
                            backgroundColor: "#f8fafc",
                            borderTop: "1px solid #f1f5f9",
                            fontSize: "10.5px",
                            color: "#64748b",
                          }}
                        >
                          <span>
                            🇵🇰 <em>Delivery available nationwide across Pakistan</em>
                          </span>
                          <button
                            type="button"
                            onClick={() => setCityDropdownOpen(false)}
                            className="btn btn-link p-0 text-decoration-none"
                            style={{ fontSize: "10.5px", color: "#2563eb", fontWeight: "600" }}
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Dynamic Delivery Live Feedback Card */}
                    {formData.city && (
                      <div
                        className="mt-2 p-2 px-3 d-flex align-items-center justify-content-between rounded-3"
                        style={{
                          background: dynamicShipping.isFreeOrder
                            ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)"
                            : "linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%)",
                          border: dynamicShipping.isFreeOrder
                            ? "1px solid #bbf7d0"
                            : "1px solid #bfdbfe",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="d-flex align-items-center justify-content-center rounded-circle"
                            style={{
                              width: "24px",
                              height: "24px",
                              backgroundColor: dynamicShipping.isFreeOrder ? "#16a34a" : "#2563eb",
                              color: "#ffffff",
                              flexShrink: 0,
                            }}
                          >
                            <FaTruck size={11} />
                          </div>
                          <div>
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "700",
                                color: dynamicShipping.isFreeOrder ? "#15803d" : "#1e40af",
                                lineHeight: 1.2,
                              }}
                            >
                              {dynamicShipping.isFreeOrder ? (
                                <span>🎉 Free Delivery to {formData.city}</span>
                              ) : (
                                <span>{dynamicShipping.zoneLabel}</span>
                              )}
                            </div>
                            <div style={{ fontSize: "10.5px", color: "#64748b" }}>
                              Est. {dynamicShipping.matchedCityObj?.estimatedDays || "2–4 Days"} • COD Available
                            </div>
                          </div>
                        </div>
                        <div>
                          <span
                            className="badge"
                            style={{
                              backgroundColor: dynamicShipping.isFreeOrder ? "#15803d" : "#1d4ed8",
                              color: "#ffffff",
                              fontSize: "11px",
                              fontWeight: "700",
                              padding: "4px 8px",
                              borderRadius: "6px",
                            }}
                          >
                            {dynamicShipping.isFreeOrder ? "FREE" : `Rs. ${dynamicShipping.standardRate}`}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label mb-1" style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>
                      Postal code <span style={{ fontWeight: 500 }}>(optional)</span>
                    </label>
                    <input
                      type="text"
                      name="postalCode"
                      className="form-control py-2 px-3"
                      style={{ fontSize: "14px", borderRadius: "8px", height: "44px" }}
                      placeholder="Postal code (optional)"
                      value={formData.postalCode}
                      onChange={handleInputChange}
                    />
                  </div>

                </div>

                <div className="mt-3 d-flex flex-column gap-2">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="saveInfo"
                      name="saveInfo"
                      checked={formData.saveInfo}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label text-muted" htmlFor="saveInfo" style={{ fontSize: "13px" }}>
                      Save this information for next time
                    </label>
                  </div>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="textNews"
                      name="textNews"
                      checked={formData.textNews}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label text-muted" htmlFor="textNews" style={{ fontSize: "13px" }}>
                      Text me with news and offers
                    </label>
                  </div>
                </div>
              </div>

              {/* 3. Shipping Method Section */}
              <div className="bg-white p-4 rounded-3 border" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                  <h5 className="fw-bold mb-0" style={{ fontSize: "17px", color: "#1e293b" }}>
                    Shipping & Delivery Method
                  </h5>
                  {formData.city ? (
                    <span className="badge bg-light text-dark border d-flex align-items-center gap-1" style={{ fontSize: "11px" }}>
                      <FaMapMarkerAlt size={10} className="text-primary" /> Delivery to {formData.city} ({dynamicShipping.zoneLabel})
                    </span>
                  ) : (
                    <span className="badge bg-light text-muted border" style={{ fontSize: "11px" }}>
                      Enter city above to calculate exact rate
                    </span>
                  )}
                </div>

                {/* Free shipping banner if qualified or progress if near */}
                {dynamicShipping.isFreeOrder ? (
                  <div className="p-2 px-3 rounded-3 mb-3 bg-success bg-opacity-10 border border-success border-opacity-25 d-flex align-items-center gap-2">
                    <FaCheckCircle className="text-success flex-shrink-0" size={14} />
                    <span className="text-success fw-semibold" style={{ fontSize: "12.5px" }}>
                      This order qualifies for free standard delivery.
                    </span>
                  </div>
                ) : dynamicShipping.freeThreshold > 0 && dynamicShipping.remainingForFree > 0 ? (
                  <div className="p-2 px-3 rounded-3 mb-3 bg-light border d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <span className="text-muted" style={{ fontSize: "12px" }}>
                      Add <strong>Rs. {Number(dynamicShipping.remainingForFree).toLocaleString()}</strong> more for free delivery.
                    </span>
                    <span className="badge bg-primary-subtle text-primary" style={{ fontSize: "10px" }}>
                      Free over Rs. {Number(dynamicShipping.freeThreshold).toLocaleString()}
                    </span>
                  </div>
                ) : null}

                {/* Address-based shipping options */}
                <div className="d-flex flex-column gap-2">
                  {/* Option 1: Standard Delivery */}
                  <label
                    className={`checkout-option p-3 d-flex align-items-center justify-content-between w-100 ${
                      formData.shippingMethod === "standard" ? "is-on" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <input
                        type="radio"
                        name="shippingMethod"
                        value="standard"
                        checked={formData.shippingMethod === "standard"}
                        onChange={handleInputChange}
                        style={{ cursor: "pointer" }}
                      />
                      <div>
                        <div className="d-flex align-items-center gap-2">
                          <span className="fw-bold" style={{ fontSize: "14px", color: "#1e293b" }}>
                            Standard Tracked Courier Delivery
                          </span>
                          {dynamicShipping.standardRate === 0 && (
                            <span className="badge bg-success text-white" style={{ fontSize: "10px" }}>
                              FREE SHIPPING
                            </span>
                          )}
                        </div>
                        <small className="text-muted d-block" style={{ fontSize: "12px" }}>
                          {formData.city
                            ? `Estimated ${dynamicShipping.matchedCityObj?.estimatedDays || "2–4 Business Days"} to ${formData.city} (${dynamicShipping.zoneLabel})`
                            : "Estimated 2–4 Business Days nationwide with tracking"}
                        </small>
                      </div>
                    </div>
                    <div className="text-end">
                      <span className={dynamicShipping.standardRate === 0 ? "fw-bold text-success fs-6" : "fw-bold text-dark fs-6"}>
                        {dynamicShipping.standardRate === 0 ? "FREE" : `Rs ${Number(dynamicShipping.standardRate).toLocaleString()}`}
                      </span>
                    </div>
                  </label>

                  {/* Option 2: Express Delivery */}
                  <label
                    className={`checkout-option p-3 d-flex align-items-center justify-content-between w-100 ${
                      formData.shippingMethod === "express" ? "is-on" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <input
                        type="radio"
                        name="shippingMethod"
                        value="express"
                        checked={formData.shippingMethod === "express"}
                        onChange={handleInputChange}
                        style={{ cursor: "pointer" }}
                      />
                      <div>
                        <div className="d-flex align-items-center gap-2">
                          <span className="fw-bold d-flex align-items-center gap-1" style={{ fontSize: "14px", color: "#1e293b" }}>
                            <FaBolt className="text-warning" size={13} /> Express Priority Courier
                          </span>
                          <span className="badge bg-warning text-dark" style={{ fontSize: "10px" }}>
                            FASTEST
                          </span>
                        </div>
                        <small className="text-muted d-block" style={{ fontSize: "12px" }}>
                          Rush packing & priority courier dispatch (1–2 Business Days)
                        </small>
                      </div>
                    </div>
                    <div className="text-end">
                      <span className="fw-bold text-dark fs-6">
                        Rs {Number(dynamicShipping.expressRate).toLocaleString()}
                      </span>
                    </div>
                  </label>
                </div>

                {!formData.city && (
                  <small className="text-muted d-block mt-2" style={{ fontSize: "11.5px" }}>
                    Delivery rates use your city and the order total.
                  </small>
                )}
              </div>

              {/* 4. Payment Section */}
              <div className="bg-white p-4 rounded-3 border" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                <h5 className="fw-bold mb-1" style={{ fontSize: "17px", color: "#1e293b" }}>
                  Payment
                </h5>
                <p className="text-muted mb-3" style={{ fontSize: "12.5px" }}>
                  {jazzcashEnabled || easypaisaEnabled
                    ? "Pay online now with your wallet or card, or choose how you would like to pay below."
                    : "Pay cash when the order arrives, or transfer the total to the store bank account."}
                </p>

                <div className="d-flex flex-column gap-2">
                  {codEnabled && (
                    <label
                      className={`checkout-option p-3 d-flex align-items-center justify-content-between ${formData.paymentMethod === "COD" ? "is-on" : ""}`}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="COD"
                          checked={formData.paymentMethod === "COD"}
                          onChange={handleInputChange}
                        />
                        <span className="fw-bold" style={{ fontSize: "14px" }}>
                          Cash on Delivery (COD)
                        </span>
                      </div>
                      <span className="badge bg-success">Pay on arrival</span>
                    </label>
                  )}

                  {[
                    jazzcashEnabled && { value: "JAZZCASH", name: "JazzCash", note: "JazzCash wallet, debit or credit card", tone: "is-jazzcash" },
                    easypaisaEnabled && { value: "EASYPAISA", name: "Easypaisa", note: "Easypaisa account, debit or credit card", tone: "is-easypaisa" },
                  ].filter(Boolean).map((option) => (
                    <label
                      key={option.value}
                      className={`checkout-option p-3 d-flex align-items-center justify-content-between ${formData.paymentMethod === option.value ? "is-on" : ""}`}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value={option.value}
                          checked={formData.paymentMethod === option.value}
                          onChange={handleInputChange}
                        />
                        <div>
                          <span className="fw-bold d-block" style={{ fontSize: "14px" }}>{option.name}</span>
                          <small className="text-muted">{option.note}. You pay on {option.name}'s secure page.</small>
                        </div>
                      </div>
                      <span className={`checkout-pay-badge ${option.tone}`}>Pay now</span>
                    </label>
                  ))}

                  {bankEnabled && (
                    <label
                      className={`checkout-option p-3 d-flex align-items-center justify-content-between ${formData.paymentMethod === "BT" ? "is-on" : ""}`}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="BT"
                          checked={formData.paymentMethod === "BT"}
                          onChange={handleInputChange}
                        />
                        <div>
                          <span className="fw-bold d-block" style={{ fontSize: "14px" }}>
                            Bank transfer
                          </span>
                          <small className="text-muted">Upload a photo of the transfer. The order stays pending until that photo is accepted.</small>
                        </div>
                      </div>
                    </label>
                  )}
                </div>

                {bankEnabled && formData.paymentMethod === "BT" && (
                  <div className="mt-3">
                    <div className="p-3 border rounded-3" style={{ background: "#f8fafc", whiteSpace: "pre-wrap", fontSize: "13px" }}>
                      {realBankDetails(storeSettings?.bankDetails) || "Transfer the order total to the store account, then upload a photo of that transfer."}
                    </div>
                    <div className="mt-3 p-3 border rounded-3 bg-white">
                      <label className="fw-bold d-block mb-1" htmlFor="payment-slip" style={{ fontSize: "14px" }}>
                        Photo of the transfer
                      </label>
                      <p className="text-muted mb-2" style={{ fontSize: "12.5px" }}>
                        A receipt or a screenshot is enough. JPG, PNG, or WEBP, up to 5 MB. The order stays pending until the store accepts this photo.
                      </p>
                      <input
                        id="payment-slip"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="form-control"
                        onChange={(event) => {
                          const file = event.target.files && event.target.files[0];
                          if (!file) {
                            setPaymentSlip(null);
                            setPaymentSlipPreview("");
                            return;
                          }
                          const allowed = ["image/jpeg", "image/png", "image/webp"];
                          if (!allowed.includes(file.type) || file.size > 5 * 1024 * 1024) {
                            setPaymentSlip(null);
                            setPaymentSlipPreview("");
                            setErrors((current) => ({
                              ...current,
                              payment_slip: "Use a JPG, PNG, or WEBP photo under 5 MB.",
                            }));
                            event.target.value = "";
                            return;
                          }
                          setPaymentSlip(file);
                          setPaymentSlipPreview(URL.createObjectURL(file));
                          setErrors((current) => ({ ...current, payment_slip: "" }));
                        }}
                      />
                      {errors.payment_slip ? (
                        <small className="text-danger d-block mt-2">{errors.payment_slip}</small>
                      ) : null}
                      {paymentSlipPreview ? (
                        <img
                          src={paymentSlipPreview}
                          alt="Selected payment photo"
                          className="mt-3 rounded border"
                          style={{ width: "160px", height: "160px", objectFit: "contain", background: "#f8fafc" }}
                        />
                      ) : null}
                    </div>
                  </div>
                )}
              </div>

              {/* 5. Billing Address Section */}
              <div className="bg-white p-4 rounded-3 border" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                <h5 className="fw-bold mb-3" style={{ fontSize: "17px", color: "#1e293b" }}>
                  Billing address
                </h5>

                <div className="border rounded-3 overflow-hidden">
                  <label
                    className="p-3 border-bottom d-flex align-items-center gap-3 w-100 cursor-pointer"
                    style={{
                      backgroundColor: formData.billingSame === "same" ? "#f8fafc" : "#fff",
                    }}
                  >
                    <input
                      type="radio"
                      name="billingSame"
                      value="same"
                      checked={formData.billingSame === "same"}
                      onChange={handleInputChange}
                    />
                    <span style={{ fontSize: "14px", fontWeight: 500 }}>Same as shipping address</span>
                  </label>

                  <label
                    className="p-3 d-flex align-items-center gap-3 w-100 cursor-pointer"
                    style={{
                      backgroundColor: formData.billingSame === "different" ? "#f8fafc" : "#fff",
                    }}
                  >
                    <input
                      type="radio"
                      name="billingSame"
                      value="different"
                      checked={formData.billingSame === "different"}
                      onChange={handleInputChange}
                    />
                    <span style={{ fontSize: "14px", fontWeight: 500 }}>Use a different billing address</span>
                  </label>
                </div>
              </div>

              {orderError && (
                <div className="alert alert-danger py-2 px-3 small mb-2" role="alert">{orderError}</div>
              )}
              {/* Complete Order Primary Button */}
              <button
                type="submit"
                disabled={submitting}
                className="btn btn-primary w-100 py-3 rounded-3 fw-bold text-white shadow-sm"
                style={{
                  backgroundColor: "#0f172a",
                  borderColor: "#0f172a",
                  fontSize: "16px",
                  letterSpacing: "0.2px",
                }}
              >
                {submitting ? "Processing Order..." : paysOnline ? `Continue to ${onlineLabels[formData.paymentMethod]}` : "Complete order"}
              </button>

              {/* Footer Policies */}
              <div className="d-flex align-items-center justify-content-center gap-3 flex-wrap text-muted pt-2" style={{ fontSize: "12px" }}>
                <a href="#refund" className="text-muted text-decoration-underline">Refund policy</a>
                <span>•</span>
                <a href="#shipping" className="text-muted text-decoration-underline">Shipping policy</a>
                <span>•</span>
                <a href="#privacy" className="text-muted text-decoration-underline">Privacy policy</a>
                <span>•</span>
                <a href="#terms" className="text-muted text-decoration-underline">Terms of service</a>
              </div>
            </form>
          </div>

          {/* RIGHT COLUMN: Shopify Order Summary Card */}
          <div className="col-12 col-lg-5">
            <div
              className="checkout-summary bg-white p-4 rounded-3 border sticky-top"
              style={{ top: "24px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}
            >
              {/* Product Line Items */}
              <div className="d-flex flex-column gap-3 mb-4 overflow-auto" style={{ maxHeight: "360px", paddingTop: "10px" }}>
                {items.map((item) => (
                  <div key={item.id} className="d-flex align-items-center justify-content-between gap-3 pb-3 border-bottom">
                    <div className="d-flex align-items-center gap-3">
                      {/* Image with Dark Quantity Badge */}
                      <div className="position-relative" style={{ width: "64px", height: "64px", flexShrink: 0 }}>
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "8px",
                            overflow: "hidden",
                            border: "1px solid #e2e8f0",
                            backgroundColor: "#f8fafc",
                          }}
                        >
                          <img
                            src={getProductImageUrl(item.image)}
                            alt={item.name}
                            style={{ width: "100%", height: "100%", objectFit: "contain", padding: "3px" }}
                            onError={handleProductImageError}
                          />
                        </div>
                        {/* Quantity Pill Badge */}
                        <span
                          className="position-absolute badge rounded-pill bg-dark"
                          style={{
                            fontSize: "11px",
                            minWidth: "20px",
                            padding: "3px 6px",
                            top: "0",
                            right: "0",
                            zIndex: 2,
                            lineHeight: 1.2,
                          }}
                        >
                          {Number(item.qty) || 1}
                        </span>
                      </div>

                      {/* Title & Promotion Tag */}
                      <div style={{ minWidth: 0 }}>
                        <h6 className="fw-semibold mb-1 text-truncate" style={{ fontSize: "14px", color: "#1e293b", maxWidth: "200px" }}>
                          {item.name}
                        </h6>
                        {item.hasDiscount && (
                          <div className="d-flex align-items-center gap-1 text-success" style={{ fontSize: "12px", fontWeight: 600 }}>
                            <FaTag size={10} />
                            <span>{item.discountLabel || "Special Promotion"}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Price */}
                    <div className="text-end">
                      <div className="fw-bold" style={{ fontSize: "14.5px" }}>
                        Rs {Number(item.price * item.qty).toLocaleString()}
                      </div>
                      {item.hasDiscount && (
                        <div className="text-muted text-decoration-line-through" style={{ fontSize: "12px" }}>
                          Rs {Number(item.originalPrice * item.qty).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Discount Code Input Box */}
              <div className="mb-4">
                {!appliedCoupon ? (
                  <form onSubmit={handleApplyCoupon} className="d-flex gap-2">
                    <input
                      type="text"
                      className="form-control py-2 px-3"
                      placeholder="Discount code (e.g. WELCOME10)"
                      value={discountCode}
                      aria-invalid={couponMessage ? "true" : "false"}
                      onChange={(e) => {
                        setDiscountCode(e.target.value);
                        setCouponMessage("");
                      }}
                      style={{ fontSize: "13.5px", borderRadius: "8px" }}
                    />
                    <button
                      type="submit"
                      className="btn btn-secondary px-3 fw-semibold"
                      style={{ borderRadius: "8px", fontSize: "13.5px", background: "#334155", border: "none" }}
                      disabled={validatingCoupon}
                    >
                      {validatingCoupon ? "..." : "Apply"}
                    </button>
                  </form>
                ) : null}
                {!appliedCoupon && couponMessage && (
                  <small className="text-danger d-block mt-1" role="alert">{couponMessage}</small>
                )}
                {!appliedCoupon ? null : (
                  <div className="p-3 border rounded-3 bg-light d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                      <FaTag color="var(--success)" />
                      <span className="fw-bold" style={{ fontSize: "13.5px" }}>
                        {appliedCoupon.code}
                      </span>
                      <span className="badge bg-success">Applied</span>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveCoupon}
                      className="btn btn-link text-danger p-0 fw-semibold"
                      style={{ fontSize: "12px" }}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>

              {quoteError ? (
                <div className="alert alert-warning py-2 px-3 mb-3" role="alert" style={{ fontSize: "13px" }}>{quoteError}</div>
              ) : null}

              {/* Subtotal, Shipping, Grand Total */}
              <div className="d-flex flex-column gap-2 mb-3" style={{ fontSize: "14px" }}>
                <div className="d-flex justify-content-between text-muted">
                  <span>Subtotal • {cartData.totalCount} items</span>
                  <span className="fw-semibold text-dark">Rs {Number(subtotal).toLocaleString()}</span>
                </div>

                {couponDiscount > 0 && (
                  <div className="d-flex justify-content-between text-success fw-semibold">
                    <span>Coupon Promo</span>
                    <span>-Rs {Number(couponDiscount).toLocaleString()}</span>
                  </div>
                )}
                {(quote?.bundles?.applied || []).map((bundle) => (
                  <div key={bundle.id} className="d-flex justify-content-between text-success fw-semibold">
                    <span>Bundle savings · {bundle.name}{bundle.sets > 1 ? ` ×${bundle.sets}` : ""}</span>
                    <span>-Rs {Number(bundle.discount).toLocaleString()}</span>
                  </div>
                ))}

                <div className="d-flex justify-content-between text-muted">
                  <div>
                    <span>Shipping</span>
                    {formData.city ? (
                      <small className="text-secondary d-block" style={{ fontSize: "11.5px" }}>
                        {formData.shippingMethod === "express" ? "Express Courier" : "Standard Delivery"} to {formData.city}
                      </small>
                    ) : (
                      <small className="text-muted d-block" style={{ fontSize: "11px" }}>
                        Calculated from shipping address
                      </small>
                    )}
                  </div>
                  <span className={dynamicShippingFee === 0 ? "fw-bold text-success" : "fw-bold text-dark"}>
                    {dynamicShippingFee === 0 ? "FREE" : `Rs ${Number(dynamicShippingFee).toLocaleString()}`}
                  </span>
                </div>

                <hr className="my-2" />

                <div className="d-flex justify-content-between align-items-baseline">
                  <div>
                    <span className="fw-bold" style={{ fontSize: "17px", color: "#0f172a" }}>
                      Total
                    </span>
                    <span className="text-muted ms-2" style={{ fontSize: "12px" }}>PKR</span>
                  </div>
                  <span className="fw-extrabold" style={{ fontSize: "22px", color: "#0f172a", opacity: quoteLoading ? 0.5 : 1, transition: "opacity .15s" }} aria-busy={quoteLoading}>
                    Rs {Number(grandTotal).toLocaleString()}
                  </span>
                </div>

                {/* Total Savings Callout (Matching Jenpharm Screenshot) */}
                {totalCombinedSavings > 0 && (
                  <div className="p-2 rounded-2 bg-success-subtle d-flex align-items-center gap-2 text-success mt-2">
                    <FaTag size={13} />
                    <span className="fw-bold" style={{ fontSize: "13.5px" }}>
                      TOTAL SAVINGS Rs {Number(totalCombinedSavings).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>

              {/* Trust Badges */}
              <div className="mt-4 pt-3 border-top d-flex flex-column gap-2 text-muted" style={{ fontSize: "12px" }}>
                <div className="d-flex align-items-center gap-2">
                  <FaCheckCircle color="var(--success)" />
                  <span>Free nationwide delivery on all orders</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <FaShieldAlt color="var(--accent)" />
                  <span>256-Bit SSL Encrypted & Protected Checkout</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
